import { NextPageContext } from 'next'
import Head from 'next/head'
import Pagination from '#/components/Pagination'
import { host } from '#/configs'
import { getAllPostsForHome } from '#/containers/Blog/lib/api'
import BreadCrumbs from '../AboutCompany/components/BreadCrumbs/BreadCrumbs'
import Container from './components/container'
import HeroPost from './components/hero-post'
import Intro from './components/intro'
import Layout from './components/layout'
import MoreStories from './components/more-stories'
import SubContainer from './components/sub-container'
import SubPosts from './components/sub-posts'
import { POSTS_PER_PAGE } from './lib/constants'
import styles from './styles.module.scss'

type AllPosts = {
  props:{
    allPosts: { edges: Blog.AllPosts; pageInfo: Blog.AllPosts }
    categories?: { edges: Blog.Categories }
    preview: boolean
    page: number
    path: string
    totalPages: number
  }
}

export default function Index({
  props: {
    allPosts: { edges },
    preview,
    page,
    path,
    totalPages
  }
}: AllPosts) {
  const allPosts = edges
  const heroPost = allPosts[0]?.node
  const subPosts = allPosts.slice(1, 3)
  const morePosts = allPosts.slice(3, 9)

  const meta = {
    title: 'Explore Real Estate Analytics | TopHap Blog',
    openGraph: {
      opengraphTitle: 'Explore Real Estate Analytics | TopHap Blog',
      opengraphDescription:
      'Learn how to use real estate analytics to assess properties at scale. Read about current real estate trends and how TopHap can help you spot them. Read now!'
    },
    image: `${host}/images/blog/tophap-blog.jpg`,
    site_name: 'Explore Real Estate Analytics | TopHap Blog'
  }
  return (
    <div className={styles['th-blog-page']}>
      <Head>
        <meta
          key='description'
          content='TopHap is the premiere real estate analytics research platform. Learn more about our mission, team, and secret sauce.'
          name='description'
        />
        <>
          {(totalPages || 0) > page ? <link href={`${path}?page=${page + 1}`} rel='next' /> : null}
          {(page > 1 && page <= (totalPages || 0)) ? <link href={`${path}${page - 1 === 1 ? '' : `?page=${page - 1}`}`} rel='prev' /> : null}
          <link key='canonical' href={`${path}${page === 1 ? '' : `?page=${page}`}`} rel='canonical' />
        </>
      </Head>
      <Layout meta={meta} preview={preview}>
        <div className='bg-gray-100'>
          <div className='flex h-12 items-center overflow-visible'>
            <header className='mx-auto flex w-full items-center justify-between px-5 lg:max-w-6xl xl:px-2'>
              <div className='py-1'>
                <BreadCrumbs homeName='Home' pageName='Blog' />
              </div>
            </header>
          </div>
          <Container>
            <SubContainer>
              <div className='mx-auto flex max-w-sm items-center justify-between py-4 sm:pt-6 md:max-w-none md:pt-8 lg:pb-5'>
                <Intro heading='Latest Stories' />
              </div>
              {(allPosts && page > 1) && (
                <div className='mx-auto px-5 sm:px-5 md:max-w-3xl lg:px-2 xl:max-w-6xl'>
                  <MoreStories posts={allPosts} />
                </div>
              )}
              {(heroPost && page === 1) && (
                <HeroPost
                  author={heroPost.author?.node}
                  category={heroPost.categories?.edges[0].node.name}
                  coverImage={heroPost.featuredImage?.node}
                  date={heroPost.date}
                  excerpt={heroPost.excerpt}
                  slug={heroPost.slug}
                  title={heroPost.title}
                />
              )}
              {(subPosts.length > 0 && page === 1) && <SubPosts posts={subPosts} />}
              {(morePosts.length > 0 && page === 1) && <MoreStories posts={morePosts} />}
            </SubContainer>

            <div>
              <div className='pb-10 pt-8'>
                <Pagination
                  active={page}
                  pageCount={totalPages}
                  path={path}
                />
              </div>
            </div>
          </Container>
        </div>
      </Layout>
    </div>
  )
}

Index.getInitialProps = async ({ query, asPath }: NextPageContext) => {
  const pageNo = isNaN(Number(query.page)) ? 1 : Number(query.page)
  const allPosts = await getAllPostsForHome(false, pageNo, POSTS_PER_PAGE, null, null)
  return {
    props: {
      allPosts,
      preview: false,
      page: pageNo,
      path: asPath?.split('?')[0],
      totalPages: Math.ceil(Number(allPosts.pageInfo.offsetPagination.total / POSTS_PER_PAGE))
    },
    revalidate: 1
  }
}
